import React from 'react';
import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';
import { HelmetProvider } from 'react-helmet-async';
import { Toaster } from './components/ui/sonner';
import { LanguageProvider } from './context/LanguageContext';
import { AuthProvider } from './context/AuthContext';
import { AnalyticsProvider } from './components/AnalyticsProvider';
import Layout from './components/layout/Layout';

// Public Pages
import HomePage from './pages/HomePage';
import AboutPage from './pages/AboutPage';
import ContactPage from './pages/ContactPage';
import JobsPage from './pages/JobsPage';
import JobDetailPage from './pages/JobDetailPage';
import JobApplicationPage from './pages/JobApplicationPage';
import CoursesPage from './pages/CoursesPage';
import CourseDetailPage from './pages/CourseDetailPage';
import RecruitmentRequestPage from './pages/RecruitmentRequestPage';
import CustomizedTrainingRequestPage from './pages/CustomizedTrainingRequestPage';
import TalentPoolPage from './pages/TalentPoolPage';

// Service Pages
import RecruitmentServicePage from './pages/services/RecruitmentServicePage';
import TrainingServicePage from './pages/services/TrainingServicePage';
import ConsultingServicePage from './pages/services/ConsultingServicePage';
import CareerAdvisoryPage from './pages/services/CareerAdvisoryPage';

// Auth Pages
import LoginPage from './pages/auth/LoginPage';
import RegisterPage from './pages/auth/RegisterPage';
import AuthCallback from './pages/auth/AuthCallback';

// Learner Pages (LMS)
import LearnerDashboard from './pages/learner/LearnerDashboard';
import CoursePlayer from './pages/learner/CoursePlayer';

// Admin Pages
import AdminLayout from './pages/admin/AdminLayout';
import AdminDashboard from './pages/admin/AdminDashboard';
import AdminJobsPage from './pages/admin/AdminJobsPage';
import AdminCategoriesPage from './pages/admin/AdminCategoriesPage';
import AdminApplicationsPage from './pages/admin/AdminApplicationsPageNew';
import AdminRecruitmentRequestsPage from './pages/admin/AdminRecruitmentRequestsPage';
import AdminTrainingRequestsPage from './pages/admin/AdminTrainingRequestsPage';
import AdminInquiriesPage from './pages/admin/AdminInquiriesPage';
import AdminTalentPoolPage from './pages/admin/AdminTalentPoolPage';
import AdminCandidatesPage from './pages/admin/AdminCandidatesPage';
import AdminATSJobsPage from './pages/admin/AdminATSJobsPage';
import AdminUsersPage from './pages/admin/AdminUsersPage';
import AdminEnrollmentsPage from './pages/admin/AdminEnrollmentsPage';
import AdminTrainingCitiesPage from './pages/admin/AdminTrainingCitiesPage';
import AdminCourseScheduleRequestsPage from './pages/admin/AdminCourseScheduleRequestsPage';
import AdminCourseTemplatesPage from './pages/admin/AdminCourseTemplatesPage';
import AdminCourseSessionsPage from './pages/admin/AdminCourseSessionsPage';
import AdminBulkSchedulePage from './pages/admin/AdminBulkSchedulePage';
import AdminYearlySchedulePage from './pages/admin/AdminYearlySchedulePage';
import AdminEnrollmentRequestsPage from './pages/admin/AdminEnrollmentRequestsPage';
import AdminConfirmedEnrollmentsPage from './pages/admin/AdminConfirmedEnrollmentsPage';
import TNAFormPage from './pages/TNAFormPage';
import TrainerEvaluationPage from './pages/TrainerEvaluationPage';
import CourseEvaluationPage from './pages/CourseEvaluationPage';

// Interview Response Pages (Public, No Layout)
import InterviewResponsePage from './pages/InterviewResponsePage';
import InterviewReschedulePage from './pages/InterviewReschedulePage';

// Insights Pages
import InsightsListPage from './pages/InsightsListPage';
import InsightDetailPage from './pages/InsightDetailPage';
import AdminInsightsPage from './pages/admin/AdminInsightsPage';

// CMS Admin Pages
import AdminCMSServicesPage from './pages/admin/AdminCMSServicesPage';
import AdminCMSClientsPage from './pages/admin/AdminCMSClientsPage';
import AdminCMSPartnersPage from './pages/admin/AdminCMSPartnersPage';
import AdminCMSPageContentPage from './pages/admin/AdminCMSPageContentPage';

// Legal Pages
import PrivacyPolicyPage from './pages/PrivacyPolicyPage';
import TermsConditionsPage from './pages/TermsConditionsPage';
import CookiePolicyPage from './pages/CookiePolicyPage';

// Candidate Portal Pages
import CandidateAccessPage from './pages/CandidateAccessPage';
import CandidatePortalPage from './pages/CandidatePortalPage';

import './App.css';

function App() {
  return (
    <HelmetProvider>
      <Router>
        <AnalyticsProvider>
          <LanguageProvider>
            <AuthProvider>
              <Routes>
            {/* Auth Routes - No Layout */}
            <Route path="/login" element={<LoginPage />} />
            <Route path="/register" element={<RegisterPage />} />
            <Route path="/auth/callback" element={<AuthCallback />} />
            
            {/* Public TNA Form - No Layout */}
            <Route path="/tna/:enrollmentId" element={<TNAFormPage />} />
            
            {/* Public Evaluation Forms - No Layout */}
            <Route path="/trainer-evaluation/:enrollmentId" element={<TrainerEvaluationPage />} />
            <Route path="/course-evaluation/:enrollmentId" element={<CourseEvaluationPage />} />
            
            {/* Candidate Portal - No Layout */}
            <Route path="/candidate-access" element={<CandidateAccessPage />} />
            <Route path="/my-applications" element={<CandidatePortalPage />} />
            
            {/* Interview Response Pages - Public, No Layout */}
            <Route path="/interview-response/:token" element={<InterviewResponsePage />} />
            <Route path="/interview-response/:token/reschedule" element={<InterviewReschedulePage />} />

            {/* Admin Routes - Admin Layout */}
            <Route path="/admin" element={<AdminLayout />}>
              <Route index element={<AdminDashboard />} />
              {/* ATS / Recruitment */}
              <Route path="candidates" element={<AdminCandidatesPage />} />
              <Route path="ats-jobs" element={<AdminATSJobsPage />} />
              <Route path="applications" element={<AdminApplicationsPage />} />
              <Route path="recruitment-requests" element={<AdminRecruitmentRequestsPage />} />
              <Route path="talent-pool" element={<AdminTalentPoolPage />} />
              {/* Training Operations */}
              <Route path="course-templates" element={<AdminCourseTemplatesPage />} />
              <Route path="course-sessions" element={<AdminCourseSessionsPage />} />
              <Route path="bulk-schedule" element={<AdminBulkSchedulePage />} />
              <Route path="yearly-schedule" element={<AdminYearlySchedulePage />} />
              <Route path="enrollment-requests" element={<AdminEnrollmentRequestsPage />} />
              <Route path="confirmed-enrollments" element={<AdminConfirmedEnrollmentsPage />} />
              <Route path="training-requests" element={<AdminTrainingRequestsPage />} />
              <Route path="schedule-requests" element={<AdminCourseScheduleRequestsPage />} />
              {/* Configuration */}
              <Route path="jobs" element={<AdminJobsPage />} />
              <Route path="categories" element={<AdminCategoriesPage />} />
              <Route path="training-cities" element={<AdminTrainingCitiesPage />} />
              <Route path="inquiries" element={<AdminInquiriesPage />} />
              <Route path="users" element={<AdminUsersPage />} />
              <Route path="enrollments" element={<AdminEnrollmentsPage />} />
              {/* CMS - Website Content */}
              <Route path="cms/services" element={<AdminCMSServicesPage />} />
              <Route path="cms/clients" element={<AdminCMSClientsPage />} />
              <Route path="cms/partners" element={<AdminCMSPartnersPage />} />
              <Route path="cms/page-content" element={<AdminCMSPageContentPage />} />
              {/* WeDo Insights */}
              <Route path="insights" element={<AdminInsightsPage />} />
            </Route>

            {/* Learner Routes (LMS) */}
            <Route path="/learner" element={<LearnerDashboard />} />
            <Route path="/learner/course/:courseId" element={<CoursePlayer />} />

            {/* Public Routes - Main Layout */}
            <Route path="/" element={<Layout><HomePage /></Layout>} />
            <Route path="/about" element={<Layout><AboutPage /></Layout>} />
            <Route path="/contact" element={<Layout><ContactPage /></Layout>} />
            
            {/* Jobs */}
            <Route path="/jobs" element={<Layout><JobsPage /></Layout>} />
            <Route path="/jobs/:jobId" element={<Layout><JobDetailPage /></Layout>} />
            <Route path="/jobs/:jobId/apply" element={<Layout><JobApplicationPage /></Layout>} />
            <Route path="/talent-pool" element={<Layout><TalentPoolPage /></Layout>} />
            
            {/* Courses */}
            <Route path="/courses" element={<Layout><CoursesPage /></Layout>} />
            <Route path="/courses/:courseId" element={<Layout><CourseDetailPage /></Layout>} />
            
            {/* Insights */}
            <Route path="/insights" element={<Layout><InsightsListPage /></Layout>} />
            <Route path="/insights/:slug" element={<Layout><InsightDetailPage /></Layout>} />
            
            {/* Services */}
            <Route path="/services/recruitment" element={<Layout><RecruitmentServicePage /></Layout>} />
            <Route path="/services/training" element={<Layout><TrainingServicePage /></Layout>} />
            <Route path="/services/consulting" element={<Layout><ConsultingServicePage /></Layout>} />
            <Route path="/services/career-advisory" element={<Layout><CareerAdvisoryPage /></Layout>} />
            
            {/* Request Forms */}
            <Route path="/recruitment-request" element={<Layout><RecruitmentRequestPage /></Layout>} />
            <Route path="/customized-training-request" element={<Layout><CustomizedTrainingRequestPage /></Layout>} />
            
            {/* Legal Pages */}
            <Route path="/privacy" element={<Layout><PrivacyPolicyPage /></Layout>} />
            <Route path="/terms" element={<Layout><TermsConditionsPage /></Layout>} />
            <Route path="/cookies" element={<Layout><CookiePolicyPage /></Layout>} />
          </Routes>
          <Toaster position="top-right" richColors />
        </AuthProvider>
      </LanguageProvider>
      </AnalyticsProvider>
    </Router>
    </HelmetProvider>
  );
}

export default App;
